import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import SearchableDropdown from '../../../components/common/SearchableDropdown';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const payeeTypes = [
  { value: 'SUPPLIER', label: 'SUPPLIER' },
  { value: 'BROKER', label: 'BROKER' },
  { value: 'CHA', label: 'CHA' },
  { value: 'DUTY', label: 'DUTY' },
  { value: 'OTHER', label: 'OTHER' },
];

const PaymentForm = () => {
  const [formData, setFormData] = useState({
    purchase_contract_id: '',
    payee_type: '',
    payee_id: '',
    party_name: '',
    invoice_date: '',
    invoice_no: '',
    amount: '',

    payment_date: '',
    utr_no: '',
    ifsc_code: '',
    bank_name: '',
  });
  const [orderOptions, setOrderOptions] = useState([]);
  const [fetchingOrders, setFetchingOrders] = useState(false);
  const [loading, setLoading] = useState(false);
  // const [message, setMessage] = useState(null); // No longer needed for errors, only for success if you want
  const navigate = useNavigate();
  // Add state for party options and loading
  const [partyOptions, setPartyOptions] = useState([]);
  const [fetchingParty, setFetchingParty] = useState(false);

  const FIELD_LIMITS = {
    purchase_contract_id: 10, // int, UI limit
    payee_type: 20, // enum
    payee_id: 10, // int, UI limit
    party_name: 120,
    invoice_date: 10, // yyyy-mm-dd
    invoice_no: 60,
    amount: 12, // decimal(12,2), input type number

    payment_date: 10, // yyyy-mm-dd
    utr_no: 60,
    ifsc_code: 20,
    bank_name: 120,
  };

  const validateField = (name, value) => {
    let error = '';
    const limit = FIELD_LIMITS[name];
    if (limit && value && value.length > limit) {
      error = `Maximum ${limit} characters allowed for ${name.replace('_', ' ')}`;
    }
    // Required fields - based on form requirements, not database schema
    const requiredFields = [
      'purchase_contract_id', 'payee_type', 'payee_id', 'party_name', 'invoice_date',
      'invoice_no', 'amount', 'payment_date'
    ];
    if (requiredFields.includes(name) && (!value || value === '' || value === 0)) {
      error = `${name.replace('_', ' ')} is required`;
    }
    return error;
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    if (error) {
      toast.error(error);
    }
  };

  useEffect(() => {
    const fetchImportOrders = async () => {
      setFetchingOrders(true);
      try {
        const res = await fetch(`${API_BASE_URL}/api/masters/dropdown-purchase-contract`, {
          credentials: 'include'
        });
        const data = await res.json();
        if (res.ok) {
          setOrderOptions(data.data);
        } else {
          console.error('Failed to fetch import orders:', data.error);
        }
      } catch (err) {
        console.error('Error fetching import orders:', err);
      } finally {
        setFetchingOrders(false);
      }
    };
    fetchImportOrders();
  }, []);

  // Fetch party options when payee_type changes
  useEffect(() => {
    const fetchPartyOptions = async () => {
      setPartyOptions([]);
      setFetchingParty(false);
      if (!formData.payee_type) return;
      let url = '';
      switch (formData.payee_type) {
        case 'CHA':
          url = `${API_BASE_URL}/api/masters/dropdown-cha-list`;
          break;
        case 'SUPPLIER':
          url = `${API_BASE_URL}/api/masters/dropdown-suppliers`;
          break;
        case 'BROKER':
          url = `${API_BASE_URL}/api/masters/dropdown-brokers`;
          break;
        default:
          return;
      }
      setFetchingParty(true);
      try {
        const res = await fetch(url, { credentials: 'include' });
        const data = await res.json();
        if (res.ok && Array.isArray(data.data)) {
          setPartyOptions(data.data);
        }
      } catch (err) {
        // Optionally handle error
      }
      setFetchingParty(false);
    };
    fetchPartyOptions();
    // Reset party_name and payee_id when payee_type changes
    setFormData(prev => ({ ...prev, party_name: '', payee_id: '' }));
    // eslint-disable-next-line
  }, [formData.payee_type]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDropdownChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    // setMessage(null);
    // Validation: check all fields for max length
    for (const key in formData) {
      if (FIELD_LIMITS[key] && formData[key] && formData[key].length > FIELD_LIMITS[key]) {
        toast.error(`Maximum ${FIELD_LIMITS[key]} characters allowed for ${key.replace('_', ' ')}`);
        setLoading(false);
        return;
      }
    }
    // Validation: check required fields
    const requiredFields = [
      'purchase_contract_id', 'payee_type', 'payee_id', 'party_name', 'invoice_date',
      'invoice_no', 'amount', 'payment_date'
    ];
    for (const field of requiredFields) {
      if (!formData[field] || formData[field] === '' || formData[field] === 0) {
        toast.error(`${field.replace('_', ' ')} is required`);
        setLoading(false);
        return;
      }
    }
    try {
      const res = await fetch(`${API_BASE_URL}/api/payments/payment-post`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success('Payment Created Successfully!');
        setFormData({
          purchase_contract_id: '',
          payee_type: '',
          payee_id: '',
          party_name: '',
          invoice_date: '',
          invoice_no: '',
          amount: '',

          payment_date: '',
          utr_no: '',
          ifsc_code: '',
          bank_name: '',
        });
      } else {
        toast.error(data.error || 'Something went wrong');
      }
    } catch (err) {
      toast.error('Server Error');
    }
    setLoading(false);
  };

  return (
    <div className="max-w-8xl mx-auto p-0 bg-white">
      <div className="flex justify-between items-center border-b border-gray-100 bg-gray-50 px-3 py-1.5">
        <h2 className="text-[15px] font-bold mb-0 text-left">Payment Entry</h2>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-[#15803d] border border-[#15803d] px-2 py-0.5 rounded text-[12px] font-medium hover:bg-[#f0fdf4]"
        >
          <FaArrowLeft />
          Back
        </button>
      </div>
      <form onSubmit={handleSubmit} className="space-y-0">
        <div className="px-3 pt-4 pb-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-[12px] font-medium mb-1">Purchase Contract Number</label>
              <SearchableDropdown
                options={orderOptions.map(order => ({
                  id: order.id,
                  name: `${order.id} (${order.purchase_contract_no})`
                }))}
                value={formData.purchase_contract_id}
                onChange={(value) => handleDropdownChange('purchase_contract_id', value)}
                placeholder="Select an order"
                displayKey="name"
                valueKey="id"
                required
                disabled={fetchingOrders}
                onBlur={handleBlur}
              />
              {fetchingOrders && (
                <p className="text-[12px] text-gray-500 mt-1">Loading orders...</p>
              )}
            </div>
            <div>
              <label className="block text-[12px] font-medium mb-1">Payee Type</label>
              <select
                name="payee_type"
                value={formData.payee_type}
                onChange={handleChange}
                onBlur={handleBlur}
                className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none"
                required
              >
                <option value="">Select payee type</option>
                {payeeTypes.map((type) => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[12px] font-medium mb-1">Payee ID</label>
              <input
                type="number"
                name="payee_id"
                value={formData.payee_id}
                onChange={handleChange}
                onBlur={handleBlur}
                className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none"
                required
              />
            </div>
            <div>
              <label className="block text-[12px] font-medium mb-1">Party Name</label>
              {['CHA', 'SUPPLIER', 'BROKER'].includes(formData.payee_type) ? (
                <SearchableDropdown
                  options={partyOptions.map(opt => ({
                    id: opt.cma_id || opt.supplier_id || opt.broker_id || opt.id || opt._id || opt.id,
                    name: opt.name || opt.party_name || opt.supplier_name || opt.broker_name || opt.name
                  }))}
                  value={formData.payee_id}
                  onChange={val => {
                    const selected = partyOptions.find(opt =>
                      (opt.cma_id || opt.supplier_id || opt.broker_id || opt.id || opt._id || opt.id) === val
                    );
                    setFormData(prev => ({
                      ...prev,
                      payee_id: val,
                      party_name: selected ? (selected.name || selected.party_name || selected.supplier_name || selected.broker_name || selected.name) : ''
                    }));
                  }}
                  placeholder={fetchingParty ? 'Loading...' : 'Select Party'}
                  displayKey="name"
                  valueKey="id"
                  required
                  disabled={fetchingParty}
                  onBlur={handleBlur}
                />
              ) : (
                <input
                  type="text"
                  name="party_name"
                  value={formData.party_name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  maxLength={FIELD_LIMITS.party_name}
                  className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none"
                  required
                />
              )}
            </div>
            <div>
              <label className="block text-[12px] font-medium mb-1">Invoice Date</label>
              <input
                type="date"
                name="invoice_date"
                value={formData.invoice_date}
                onChange={handleChange}
                onBlur={handleBlur}
                className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none"
                required
              />
            </div>
            <div>
              <label className="block text-[12px] font-medium mb-1">Invoice No</label>
              <input
                type="text"
                name="invoice_no"
                value={formData.invoice_no}
                onChange={handleChange}
                onBlur={handleBlur}
                maxLength={FIELD_LIMITS.invoice_no}
                className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none"
                required
              />
            </div>
            <div>
              <label className="block text-[12px] font-medium mb-1">Amount</label>
              <input
                type="number"
                step="0.01"
                name="amount"
                value={formData.amount}
                onChange={handleChange}
                onBlur={handleBlur}
                className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none"
                required
              />
            </div>

            <div>
              <label className="block text-[12px] font-medium mb-1">Payment Date</label>
              <input
                type="date"
                name="payment_date"
                value={formData.payment_date}
                onChange={handleChange}
                onBlur={handleBlur}
                className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none"
                required
              />
            </div>
            <div>
              <label className="block text-[12px] font-medium mb-1">UTR / Reference No.</label>
              <input
                type="text"
                name="utr_no"
                value={formData.utr_no}
                onChange={handleChange}
                onBlur={handleBlur}
                maxLength={FIELD_LIMITS.utr_no}
                className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none"
              />
            </div>
            <div>
              <label className="block text-[12px] font-medium mb-1">IFSC Code</label>
              <input
                type="text"
                name="ifsc_code"
                value={formData.ifsc_code}
                onChange={handleChange}
                onBlur={handleBlur}
                maxLength={FIELD_LIMITS.ifsc_code}
                className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none"
              />
            </div>
            <div>
              <label className="block text-[12px] font-medium mb-1">Bank Name</label>
              <input
                type="text"
                name="bank_name"
                value={formData.bank_name}
                onChange={handleChange}
                onBlur={handleBlur}
                maxLength={FIELD_LIMITS.bank_name}
                className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none"
              />
            </div>
          </div>
        </div>
        <div className="px-3 pb-4 flex gap-2">
          <button
            type="submit"
            className="bg-[#15803d] text-white px-4 py-1.5 rounded text-[13px] font-semibold hover:bg-[#166534] border border-[#15803d] disabled:opacity-60"
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Payment'}
          </button>
          <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick pauseOnFocusLoss draggable pauseOnHover />
        </div>
      </form>
    </div>
  );
};

export default PaymentForm;
